/**
 * File: clientsService.ts
 * Path: apps/admin/src/shared/services/clientsService.ts
 * Description: Every read and write the Clients pages make. Clients won from
 * a lead are created by the leads repository's stage move (same id, same
 * transaction); this module adds clients by hand, edits them and links their
 * case studies.
 *
 * Like the leads repository, writes take Firestore from a getter so the rules
 * tests can run these exact writes against the emulator.
 *
 * The client's own history lives in clients/<id>/activity: append-only,
 * signed by its author, like a lead's.
 *
 * Client numbers (C001...) are handed out here and in the leads repository's
 * Won move, through clientNumbering.ts.
 */

import {
  BUSINESSES_COLLECTION,
  businessFolderName,
  clientJobFields,
  consentWrite,
  describeConsent,
  clientJobsPath,
  clientsToNumber,
  CLIENTS_LIST_LIMIT,
  db,
  FIRESTORE_COLLECTIONS,
  FirestoreService,
  limit,
  orderBy,
  toClient,
  type ClientType,
  toClientActivity,
  toClientJob,
  type Client,
  type Consent,
  type ClientActivity,
  type ClientActivityType,
  type ClientDetails,
  type ClientJob,
  type ClientJobDetails,
} from "@idk/config";
import type { AuthoritativePortfolioProject } from "@idk/types";
import {
  arrayRemove,
  arrayUnion,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  type Firestore,
} from "firebase/firestore";
import {
  claimClientNumber,
  clientCounterRef,
  readClientCounter,
  readClientInTx,
} from "./clientNumberingService";
import { businessJobsPath, recordHome } from "./businessesService";
import {
  businessFieldsForType,
  businessToClient,
  clientsUnion,
  legacyIdsOf,
  type Row,
} from "../utils/businessRecordUtils";

const CLIENTS = FIRESTORE_COLLECTIONS.clients;
/** Well under Firestore's 500 writes per batch, leaving room for the client. */
const REMOVE_BATCH = 400;

const BUSINESSES = BUSINESSES_COLLECTION;

/**
 * A client is a business once the move has reached it, else the old client
 * document. `home` is the collection that holds it.
 */
const activityPath = (home: string, clientId: string) =>
  `${home}/${clientId}/activity`;
const jobsPathIn = (home: string, clientId: string) =>
  home === BUSINESSES ? businessJobsPath(clientId) : clientJobsPath(clientId);

type Stored = Record<string, unknown> & { id: string };

/** Who is writing a history entry (the same as the leads repository's). */
export interface ClientActor {
  uid: string;
  email: string;
}

/**
 * What the client page shows of each project. `status` is null when the
 * signed-in person may not read the project: the portfolio rules only let
 * content staff read projects that aren't on the website, so for sales
 * staff "null" means "not on the website".
 */
export interface ClientProject {
  id: string;
  title: string;
  status: AuthoritativePortfolioProject["status"] | null;
  featured: boolean;
}

const isPermissionDenied = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { code?: unknown }).code === "permission-denied";

export const createClientsRepo = (getDb: () => Firestore) => {
  const homeOf = (clientId: string) => recordHome(getDb(), clientId, "clients");
  return {
    /** The union of won and past businesses and old clients no business stands for. */
    list: async (): Promise<Client[]> => {
      const [businesses, oldClients] = await Promise.all([
        FirestoreService.query<Stored>(
          BUSINESSES,
          orderBy("updatedAt", "desc"),
          limit(CLIENTS_LIST_LIMIT * 2),
        ),
        FirestoreService.query<Stored>(
          CLIENTS,
          orderBy("updatedAt", "desc"),
          limit(CLIENTS_LIST_LIMIT),
        ),
      ]);
      return clientsUnion(businesses, oldClients);
    },

    /** The business when there is one, else the old client. */
    get: async (id: string): Promise<Client | null> => {
      const business = await FirestoreService.getById<Stored>(BUSINESSES, id);
      if (business) return businessToClient(business);
      const row = await FirestoreService.getById<Stored>(CLIENTS, id);
      return row ? toClient(row.id, row) : null;
    },

    /**
     * The client's projects, in the order they were linked. A project deleted
     * from the portfolio is left out rather than shown broken; one the viewer
     * may not read comes back with `status: null` (see ClientProject).
     */
    projects: async (projectIds: string[]): Promise<ClientProject[]> => {
      const rows = await Promise.allSettled(
        projectIds.map((id) =>
          FirestoreService.getById<AuthoritativePortfolioProject>(
            FIRESTORE_COLLECTIONS.portfolio,
            id,
          ),
        ),
      );
      return rows.flatMap((row, index): ClientProject[] => {
        if (row.status === "rejected") {
          if (!isPermissionDenied(row.reason)) throw row.reason;
          return [
            { id: projectIds[index], title: "", status: null, featured: false },
          ];
        }
        if (row.value === null) return [];
        const { id, title, status, featured } = row.value;
        return [{ id, title, status, featured: featured === true }];
      });
    },

    /** The client's notes and logged calls, messages and meetings, newest first. */
    activity: async (clientId: string): Promise<ClientActivity[]> => {
      const rows = await FirestoreService.query<Stored>(
        activityPath(await homeOf(clientId), clientId),
        orderBy("createdAt", "desc"),
        limit(200),
      );
      return rows.map((row) => toClientActivity(row.id, row));
    },

    /**
     * A client from before the pipeline, or won some other way. Gets the next
     * client number once numbering has started (see numberExisting).
     */
    create: async (details: ClientDetails): Promise<string> => {
      const db = getDb();
      const ref = doc(collection(db, BUSINESSES));
      await runTransaction(db, async (tx) => {
        const last = await readClientCounter(tx, db);
        const clientNumber =
          last === null ? null : claimClientNumber(tx, db, last, ref.id);
        tx.set(ref, {
          ...details,
          ...(clientNumber === null ? {} : { clientNumber }),
          stage: "won",
          legacy: { leadId: null, clientId: null },
          folder: businessFolderName({
            id: ref.id,
            name: details.name || "business",
            clientNumber,
          }),
          projectIds: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
      return ref.id;
    },

    /**
     * "Number existing clients": gives every client without a number the next
     * one, oldest first (clientsToNumber), one transaction each. Safe to run
     * again: a client that has a number is skipped, so nobody is renumbered,
     * and a run cut short carries on where it stopped. The first run creates
     * counters/clients, after which new clients are numbered as they are added.
     */
    numberExisting: async (): Promise<{ numbered: number; total: number }> => {
      const db = getDb();
      const counter = clientCounterRef(db);
      await runTransaction(db, async (tx) => {
        if (!(await tx.get(counter)).exists()) tx.set(counter, { last: 0 });
      });
      const [businesses, oldClients] = await Promise.all([
        getDocs(collection(db, BUSINESSES)),
        getDocs(collection(db, CLIENTS)),
      ]);
      const asRows = (set: typeof businesses): Row[] =>
        set.docs.map((d) => ({ id: d.id, ...d.data() }));
      const everyone = clientsUnion(asRows(businesses), asRows(oldClients));
      const todo = clientsToNumber(everyone);
      let numbered = 0;
      for (const client of todo) {
        // One at a time, in order: each takes the number after the last one.
        const given = await runTransaction(db, async (tx) => {
          const last = (await readClientCounter(tx, db)) ?? 0;
          const fresh = await readClientInTx(tx, db, client.id);
          if (!fresh || fresh.clientNumber !== null) return false;
          const clientNumber = claimClientNumber(tx, db, last, client.id);
          tx.update(doc(db, fresh.home, client.id), { clientNumber });
          return true;
        });
        if (given) numbered += 1;
      }
      return { numbered, total: everyone.length };
    },

    update: async (id: string, details: Partial<ClientDetails>) =>
      updateDoc(doc(getDb(), await homeOf(id), id), {
        ...details,
        updatedAt: serverTimestamp(),
      }),

    /** Client, Lead, Partner or Past client. */
    setType: async (id: string, type: ClientType) => {
      const home = await homeOf(id);
      // On a business the type is its stage (and the partner flag).
      return updateDoc(
        doc(getDb(), home, id),
        home === BUSINESSES
          ? {
              ...businessFieldsForType(type),
              stageChangedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            }
          : { type, updatedAt: serverTimestamp() },
      );
    },

    /** Adds a case study to the client. Saving the same one twice is harmless. */
    linkProject: async (clientId: string, projectId: string) =>
      updateDoc(doc(getDb(), await homeOf(clientId), clientId), {
        projectIds: arrayUnion(projectId),
        updatedAt: serverTimestamp(),
      }),

    /** Takes a project off the client. The project itself is never touched. */
    unlinkProject: async (clientId: string, projectId: string) =>
      updateDoc(doc(getDb(), await homeOf(clientId), clientId), {
        projectIds: arrayRemove(projectId),
        updatedAt: serverTimestamp(),
      }),

    /**
     * Every client's jobs in one read (a collection-group query over
     * "projects"), keyed by client id. The Projects list used to run one query
     * per client. Only jobs under a client are kept.
     */
    allJobs: async (): Promise<Record<string, ClientJob[]>> => {
      const [oldJobs, newJobs, businesses] = await Promise.all([
        getDocs(collectionGroup(getDb(), "projects")),
        getDocs(collectionGroup(getDb(), "jobs")),
        getDocs(collection(getDb(), BUSINESSES)),
      ]);
      // An old client the move has reached is read from its business only.
      const shadowed = new Set(
        businesses.docs.flatMap((b) => legacyIdsOf({ id: b.id, ...b.data() })),
      );
      const byClient: Record<string, ClientJob[]> = {};
      for (const d of oldJobs.docs) {
        const clientRef = d.ref.parent.parent;
        if (
          !clientRef ||
          clientRef.parent.id !== CLIENTS ||
          shadowed.has(clientRef.id)
        )
          continue;
        (byClient[clientRef.id] ??= []).push(
          toClientJob(d.id, clientRef.id, d.data()),
        );
      }
      for (const d of newJobs.docs) {
        const businessRef = d.ref.parent.parent;
        if (!businessRef || businessRef.parent.id !== BUSINESSES) continue;
        (byClient[businessRef.id] ??= []).push(
          toClientJob(d.id, businessRef.id, d.data()),
        );
      }
      for (const jobs of Object.values(byClient))
        jobs.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
      return byClient;
    },

    /** The work done for this client (clients/<id>/projects), newest first. */
    jobs: async (clientId: string): Promise<ClientJob[]> => {
      const rows = await FirestoreService.query<Stored>(
        jobsPathIn(await homeOf(clientId), clientId),
        orderBy("createdAt", "desc"),
        limit(200),
      );
      return rows.map((row) => toClientJob(row.id, clientId, row));
    },

    addJob: async (
      clientId: string,
      details: ClientJobDetails,
    ): Promise<string> => {
      const ref = doc(
        collection(getDb(), jobsPathIn(await homeOf(clientId), clientId)),
      );
      await setDoc(ref, {
        ...clientJobFields(details),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },

    updateJob: async (
      clientId: string,
      jobId: string,
      details: ClientJobDetails,
    ): Promise<void> => {
      await updateDoc(
        doc(getDb(), jobsPathIn(await homeOf(clientId), clientId), jobId),
        {
          ...clientJobFields(details),
          updatedAt: serverTimestamp(),
        },
      );
    },

    /**
     * Deletes one project (job). Its changes and approvals go with it; the
     * contracts, invoices and time logged against it stay in the books.
     */
    removeJob: async (clientId: string, jobId: string): Promise<void> => {
      await deleteDoc(
        doc(getDb(), jobsPathIn(await homeOf(clientId), clientId), jobId),
      );
    },

    /** Points the job at its portfolio case study (or at none); nothing else changes. */
    setJobCaseStudy: async (
      clientId: string,
      jobId: string,
      portfolioId: string | null,
    ): Promise<void> => {
      await updateDoc(
        doc(getDb(), jobsPathIn(await homeOf(clientId), clientId), jobId),
        {
          portfolioId,
          updatedAt: serverTimestamp(),
        },
      );
    },

    /**
     * A note, or a call, WhatsApp, email or meeting worth remembering. Written
     * once and signed; the client moves up the "recently active" order with it.
     */
    logActivity: async (
      clientId: string,
      entry: { type: ClientActivityType; text: string },
      actor: ClientActor,
    ): Promise<void> => {
      const home = await homeOf(clientId);
      const batch = writeBatch(getDb());
      batch.set(doc(collection(getDb(), activityPath(home, clientId))), {
        type: entry.type,
        text: entry.text.trim(),
        byUid: actor.uid,
        byEmail: actor.email,
        createdAt: serverTimestamp(),
      });
      batch.update(doc(getDb(), home, clientId), {
        updatedAt: serverTimestamp(),
      });
      await batch.commit();
    },

    /** Records a consent decision, keeping the two switches in step, with a line in the history. */
    setConsent: async (
      clientId: string,
      consent: Consent,
      actor: ClientActor,
    ): Promise<void> => {
      const home = await homeOf(clientId);
      const batch = writeBatch(getDb());
      batch.update(doc(getDb(), home, clientId), {
        ...consentWrite(consent),
        updatedAt: serverTimestamp(),
      });
      batch.set(doc(collection(getDb(), activityPath(home, clientId))), {
        type: "note",
        text: `Consent: ${describeConsent(consent)}`,
        byUid: actor.uid,
        byEmail: actor.email,
        createdAt: serverTimestamp(),
      });
      await batch.commit();
    },

    /**
     * "Remove this person's details" on a client: the people (contact, phone,
     * email, other contacts) are cleared. The business, its contracts and
     * payments stay, because those records are kept for five years for SARS.
     */
    removeDetails: async (
      clientId: string,
      actor: ClientActor,
    ): Promise<void> => {
      const home = await homeOf(clientId);
      const batch = writeBatch(getDb());
      batch.update(doc(getDb(), home, clientId), {
        contactName: "",
        phone: "",
        email: "",
        contacts: [],
        branches: [],
        updatedAt: serverTimestamp(),
      });
      batch.set(doc(collection(getDb(), activityPath(home, clientId))), {
        type: "note",
        text: "Personal details removed at their request. The business and its contracts and payments are kept for 5 years.",
        byUid: actor.uid,
        byEmail: actor.email,
        createdAt: serverTimestamp(),
      });
      await batch.commit();
    },

    /** Everything held about a client's people, for a data request: the record, history and jobs. */
    exportData: async (clientId: string): Promise<Record<string, unknown>> => {
      const home = await homeOf(clientId);
      const record = await getDoc(doc(getDb(), home, clientId));
      const history = await getDocs(
        collection(getDb(), activityPath(home, clientId)),
      );
      const jobs = await getDocs(
        collection(getDb(), jobsPathIn(home, clientId)),
      );
      return {
        exportedAt: new Date().toISOString(),
        client: { id: clientId, ...(record.data() ?? {}) },
        history: history.docs.map((d) => ({ id: d.id, ...d.data() })),
        jobs: jobs.docs.map((d) => ({ id: d.id, ...d.data() })),
      };
    },

    /**
     * Removes the client with their notes, history and projects. Their lead,
     * contracts, quotes and portfolio case studies stay. The rules only let a
     * note or a project go in the same batch as its client, so every batch
     * deletes the client too.
     */
    remove: async (id: string): Promise<void> => {
      const store = getDb();
      const home = await homeOf(id);
      const clientRef = doc(store, home, id);
      const [entries, jobs] = await Promise.all([
        getDocs(collection(store, activityPath(home, id))),
        getDocs(collection(store, jobsPathIn(home, id))),
      ]);
      const refs = [...entries.docs, ...jobs.docs].map((d) => d.ref);
      for (let i = 0; i === 0 || i < refs.length; i += REMOVE_BATCH) {
        const batch = writeBatch(store);
        for (const ref of refs.slice(i, i + REMOVE_BATCH)) batch.delete(ref);
        batch.delete(clientRef);
        await batch.commit();
      }
    },
  };
};

export const clientsRepo = createClientsRepo(() => db);

export type ClientsRepo = ReturnType<typeof createClientsRepo>;
